/* pilotData.js — iter-267 · Canonical Portfolio & Data Unification.
 *
 * Single canonical source of truth for the ChainFlow pilot demo.
 * Every operational surface (Properties · Deal Rooms · Forecasting
 * Centre · Documents · Activity) reads from this module — one
 * property = one transaction = one operational truth.
 *
 * Portfolio (18 properties — calibrated to a strong London office):
 *
 *   Status mix:
 *     • 5 Sales Agreed   (exchange-imminent)
 *     • 9 Under Offer    (mid-pipeline progression)
 *     • 4 At Risk        (blocker / chain-risk scenarios)
 *
 *   Tenure mix:
 *     • 13 Leasehold     (apartments / period flats / riverside)
 *     • 5 Freehold       (townhouses / family homes)
 *
 *   Locations: Kensington · Chelsea · Islington · Canary Wharf ·
 *              Wapping · Limehouse · Greenwich · Blackheath ·
 *              Chiswick · Stratford · Docklands · plus the
 *              chain-risk Dartford holdover.
 *
 *   Agents:    Rhys Drewell · Oliver Carter · Sophie Bennett ·
 *              James Walker (consistent across every surface).
 *
 *   Fees:      DERIVED at 1.85% commission (VAT excluded) from the
 *              property value. Calling code MUST NOT hardcode a
 *              fee — read `p.fee` instead.
 *
 *   Tenure-specific journeys:
 *     • Freehold:  Offer → AML → Survey → Draft Contract →
 *                  Searches → Mortgage → Exchange → Completion
 *     • Leasehold: same + Management Pack between Draft Contract
 *                  and Searches.
 */

const HOUSE = (id) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=900&q=70`;

/* Commission anchoring — iter-271 pilot calibration: agency fees
 * vary across a 1.75% – 2.0% band per property (premium homes get
 * a softer rate, smaller homes pay the standard 2.0%). VAT is
 * still excluded from forecasting at the dataset level. The
 * canonical `PILOT_COMMISSION_RATE` constant remains at the
 * 1.85% median so any consumer that asks for the headline rate
 * still gets one clean number. Per-property overrides live on
 * the `commissionRate` field and are resolved through
 * `_rateFor()`; downstream `feeFor()` honours the override and
 * derives the fee at module load. */
export const PILOT_COMMISSION_RATE = 0.0185;
export const PILOT_COMMISSION_BAND = Object.freeze({ min: 0.0175, max: 0.0200 });
const _rateFor = (p) => {
  const r = Number(p?.commissionRate);
  return Number.isFinite(r) && r > 0 ? r : PILOT_COMMISSION_RATE;
};
const feeFor = (p) => {
  // Accept either a property object (preferred, iter-271) or a
  // raw value (legacy callers) so both code-paths stay valid.
  if (typeof p === "number") return Math.round(p * PILOT_COMMISSION_RATE);
  return Math.round((p?.value || 0) * _rateFor(p));
};

/* ─── Canonical pilot journey vocabulary ─────────────────────────── */
export const FREEHOLD_JOURNEY = [
  "Offer", "AML", "Survey", "Draft Contract",
  "Searches", "Mortgage", "Exchange", "Completion",
];
export const LEASEHOLD_JOURNEY = [
  "Offer", "AML", "Survey", "Draft Contract",
  "Mgmt Pack", "Searches", "Mortgage", "Exchange", "Completion",
];

export const STAGE_TO_MILESTONE = {
  offer:           "Offer",
  memo:            "Offer",
  aml:             "AML",
  solicitors:      "AML",
  survey:          "Survey",
  drafts:          "Draft Contract",
  mgmt_pack:       "Mgmt Pack",
  searches:        "Searches",
  enquiries:       "Searches",
  mortgage_app:    "Mortgage",
  mortgage_offer:  "Mortgage",
  exchange_ready:  "Exchange",
  exchanged:       "Exchange",
  completed:       "Completion",
};

const journeyFor = (tenure) =>
  tenure === "leasehold" ? LEASEHOLD_JOURNEY : FREEHOLD_JOURNEY;

const progressFor = (tenure, currentMilestone) => {
  const seq = journeyFor(tenure);
  const idx = seq.indexOf(currentMilestone);
  if (idx < 0) return 0;
  return Math.round(((idx + 1) / seq.length) * 100);
};

/* ─── iter-270F · Pilot Deal UUID mapping ──────────────────────────
 *
 * Pilot demo deals are frontend-only by design (no backend persistence
 * for the 18-property tour). The original slugs (`deal-greenwich`,
 * `deal-putney`, …) read well in URLs but FastAPI's UUID path-param
 * validator rejected them with "Input should be a valid UUID,
 * invalid character: found `l` at 4" when Mark Complete / journey
 * actions ran. We pin a deterministic UUID per slug here so every
 * API call carries a canonical id, and we map back to the slug via
 * `PILOT_DEAL_SLUG_BY_UUID` for tests/diagnostics.
 *
 * Slug strings are NOT used as deal_id any more — they survive on
 * the property record (`dealSlug`) for human-readable inspection in
 * the URL bar and pytest pins. The PILOT_DEAL has `id: <uuid>` and
 * `slug: <legacy slug>` so both vocabularies coexist. */
export const PILOT_DEAL_UUIDS = Object.freeze({
  "greenwich":    "c9a00001-c9a0-4c9a-8c9a-c9a000000001",
  "holland-park": "c9a00002-c9a0-4c9a-8c9a-c9a000000002",
  "putney":       "c9a00003-c9a0-4c9a-8c9a-c9a000000003",
  "earls-court":  "c9a00004-c9a0-4c9a-8c9a-c9a000000004",
  "westferry":    "c9a00005-c9a0-4c9a-8c9a-c9a000000005",
  "stratford":    "c9a00006-c9a0-4c9a-8c9a-c9a000000006",
  "lee":          "c9a00007-c9a0-4c9a-8c9a-c9a000000007",
  "islington":    "c9a00008-c9a0-4c9a-8c9a-c9a000000008",
  "chingford":    "c9a00009-c9a0-4c9a-8c9a-c9a000000009",
  "walthamstow":  "c9a0000a-c9a0-4c9a-8c9a-c9a00000000a",
  "chelsea":      "c9a0000b-c9a0-4c9a-8c9a-c9a00000000b",
  "canonbury":    "c9a0000c-c9a0-4c9a-8c9a-c9a00000000c",
  "wapping":      "c9a0000d-c9a0-4c9a-8c9a-c9a00000000d",
  "ontario":      "c9a0000e-c9a0-4c9a-8c9a-c9a00000000e",
  "canary":       "c9a0000f-c9a0-4c9a-8c9a-c9a00000000f",
  "dartford":     "c9a00010-c9a0-4c9a-8c9a-c9a000000010",
  "limehouse":    "c9a00011-c9a0-4c9a-8c9a-c9a000000011",
  "blackheath":   "c9a00012-c9a0-4c9a-8c9a-c9a000000012",
  // iter-271 — Pilot calibration pass · 4 new transactions to
  // reach the 22-property single-office target with the spec
  // monthly bankings (£31k–£65k) and 70/30 leasehold/freehold mix.
  "battersea":    "c9a00013-c9a0-4c9a-8c9a-c9a000000013",
  "kensington":   "c9a00014-c9a0-4c9a-8c9a-c9a000000014",
  "highgate":     "c9a00015-c9a0-4c9a-8c9a-c9a000000015",
  "hampstead":    "c9a00016-c9a0-4c9a-8c9a-c9a000000016",
  // iter-376 — Sample Offer deal.
  // Fresh offer-stage entry so pilot operators can walk the
  // "Mark Offer Agreed → Transaction Starts" cascade live during
  // demos without needing to seed a deal via the API. Chain +
  // mortgage buyer (default) so the cascade lands on the Mortgage
  // milestone. See _PILOT_BASE for the property record.
  "sample-offer": "c9a00017-c9a0-4c9a-8c9a-c9a000000017",
});
export const PILOT_DEAL_SLUG_BY_UUID = Object.freeze(
  Object.fromEntries(Object.entries(PILOT_DEAL_UUIDS).map(([slug, uuid]) => [uuid, slug]))
);
const _dealUuid = (slug) => PILOT_DEAL_UUIDS[slug];

/* ─── Canonical 18-property pilot portfolio ──────────────────────── */
const _PILOT_BASE = [
  // ── 5 Sales Agreed (exchange-ready) ──────────────────────────────
  {
    id: "p-greenwich", dealId: _dealUuid("greenwich"), dealSlug: "deal-greenwich",
    image: HOUSE("photo-1564013799919-ab600027ffc6"),
    address: "9 Crooms Hill",
    postcode: "SE10 8ER",
    locality: "Greenwich, London",
    value: 1250000, commissionRate: 0.01,
    beds: 4, baths: 2, parking: "Residents permit",
    tenure: "freehold",
    agent: "Rhys Drewell",
    status: "Sales Agreed", risk: "low",
    exchangeDate: "2026-08-27", completionDate: "2026-09-17",
    stage: "exchange_ready",
    currentMilestone: "Exchange",
    confidence: 92,
    progressNote: "Contracts approved by both solicitors · Cash buyer proof of funds cleared · Final tweaks before exchange",
    blockerState: "Cash Buyer Progressing",
  },
  {
    id: "p-holland-park", dealId: _dealUuid("holland-park"), dealSlug: "deal-holland-park",
    image: HOUSE("photo-1600596542815-ffad4c1539a9"),
    address: "22 Holland Park Gardens",
    postcode: "W14 8DH",
    locality: "Holland Park, London",
    value: 1650000, commissionRate: 0.013,
    beds: 4, baths: 3, parking: "Garage · 1",
    tenure: "leasehold",
    agent: "Sophie Bennett",
    status: "Sales Agreed", risk: "low",
    exchangeDate: "2026-06-04", completionDate: "2026-06-26",
    stage: "exchange_ready",
    currentMilestone: "Exchange",
    confidence: 94,
    progressNote: "Contracts signed by both sides · Buyer solicitor confirmed exchange for 4 Jun",
    blockerState: "Contracts Issued",
    // iter-380 · Stage-1 flagship pilot deal.
    // • Buyer Solicitor: Ashcroft Lyle (Priya Ashcroft) — from
    //   the shared professional network in `_PERSONA_POOL`.
    // • Seller Solicitor: Bowden Holroyd (Helena Bowden) — same pool.
    // • Managing Agent: Canonbury Management (Yasmin Al-Rashid).
    // • Mortgage Broker: Northbridge Capital (Adaeze Okoye).
    // • Complication (resolved): Buyer's LPE1 arrived with an
    //   under-declared reserve fund contribution; corrected by
    //   the managing agent on 24 May after a chase from Sophie.
    pilotFlagship: true,
    flagshipBuyer:  "Mr & Mrs Trevelyan (mortgage · Northbridge Capital)",
    flagshipSeller: "Ms Naomi Ellsworth (moving to a Somerset village · onward chain-free)",
    flagshipComplicationTitle:      "LPE1 reserve fund understated by £3,400",
    flagshipComplicationRaised:     "2026-05-15",
    flagshipComplicationResolved:   "2026-05-24",
    flagshipComplicationResolution: "Canonbury Management reissued the LPE1 with the correct s.42 apportionment. Buyer solicitor accepted the amended figures without re-raising enquiries.",
    flagshipCurrentAction: "Exchange 4 Jun · 09:30 · Both solicitors briefed on completion monies",
  },
  {
    id: "p-putney", dealId: _dealUuid("putney"), dealSlug: "deal-putney",
    image: HOUSE("photo-1568605114967-8130f3a36994"),
    address: "14 Riverside Terrace",
    postcode: "SW15 1LB",
    locality: "Putney, London",
    value: 1395000, commissionRate: 0.01,
    beds: 4, baths: 3, parking: "Driveway · 2",
    tenure: "leasehold",
    agent: "Oliver Carter",
    status: "Sales Agreed", risk: "low",
    exchangeDate: "2026-09-03", completionDate: "2026-09-24",
    stage: "exchange_ready",
    currentMilestone: "Exchange",
    confidence: 91,
    progressNote: "Riverside penthouse · BSA cladding certification cleared · Contracts approved · Ready to exchange",
    blockerState: "Ready To Exchange",
    applicableBSA: true,
  },
  {
    id: "p-earls-court", dealId: _dealUuid("earls-court"), dealSlug: "deal-earls-court",
    image: HOUSE("photo-1512917774080-9991f1c4c750"),
    address: "47 Earls Court Square",
    postcode: "SW5 9DW",
    locality: "Kensington, London",
    value: 895000, commissionRate: 0.014,
    beds: 2, baths: 2, parking: "On-street",
    tenure: "leasehold",
    agent: "Sophie Bennett",
    status: "Sales Agreed", risk: "low",
    exchangeDate: "2026-06-11", completionDate: "2026-07-02",
    stage: "exchange_ready",
    currentMilestone: "Exchange",
    confidence: 88,
    progressNote: "Period flat · contracts exchanged Wed",
    blockerState: "Ready To Exchange",
  },
  {
    id: "p-westferry", dealId: _dealUuid("westferry"), dealSlug: "deal-westferry",
    image: HOUSE("photo-1545324418-cc1a3fa10c00"),
    address: "18 Westferry Circus",
    postcode: "E14 8RW",
    locality: "Canary Wharf, London",
    value: 1550000, commissionRate: 0.013,
    beds: 3, baths: 2, parking: "Underground · 1",
    tenure: "leasehold",
    agent: "James Walker",
    status: "Sales Agreed", risk: "low",
    exchangeDate: "2026-07-02", completionDate: "2026-07-23",
    stage: "exchange_ready",
    currentMilestone: "Exchange",
    confidence: 90,
    progressNote: "Concierge development · Cladding fire safety report signed off · Contracts signed by seller",
    blockerState: "Contracts Issued",
    applicableBSA: true,
    // iter-382 · Stage-2 supporting deal · Leasehold + BSA arc.
    // • Buyer Solicitor: Fielding Westgate — TA6 + LPE1 handled
    //   cleanly.  Seller Solicitor: Marling & Partners.
    // • Managing Agent: Ashcroft & Colby (BSA-certified estate).
    // • Complication (resolved): EWS1 certificate for the north
    //   tower was refreshed at the buyer solicitor's request on
    //   3 Jun; buyer lender (Coutts) re-confirmed the mortgage
    //   offer the same afternoon.
    pilotSupporting: true,
    supportingRole: "leasehold-bsa",
    supportingBuyer:  "Mr Harun Chowdhury (Coutts mortgage · BSA-aware lender)",
    supportingSeller: "Ms Elena Faraday (relocating to Dubai · onward chain-free)",
    supportingComplicationTitle:      "EWS1 form refreshed for north tower",
    supportingComplicationRaised:     "2026-06-01",
    supportingComplicationResolved:   "2026-06-03",
    supportingComplicationResolution: "Ashcroft & Colby reissued the EWS1 with the 2026 remediation warranty attached. Coutts re-confirmed the mortgage offer within four hours.",
    supportingCurrentAction: "Exchange 2 Jul · 11:00 · Buyer + seller solicitors briefed on completion monies",
  },
  // iter-271 — Battersea (new) · Sales Agreed · leasehold · exchange Jul.
  {
    id: "p-battersea", dealId: _dealUuid("battersea"), dealSlug: "deal-battersea",
    image: HOUSE("photo-1493809842364-78817add7ffb"),
    address: "Apt 21, Battersea Power Station",
    postcode: "SW11 8BJ",
    locality: "Battersea, London",
    value: 1050000, commissionRate: 0.012,
    beds: 2, baths: 2, parking: "Underground · 1",
    tenure: "leasehold",
    agent: "Oliver Carter",
    status: "Sales Agreed", risk: "low",
    exchangeDate: "2026-07-09", completionDate: "2026-07-30",
    stage: "exchange_ready",
    currentMilestone: "Exchange",
    confidence: 87,
    progressNote: "Riverside dev · contracts approved",
    blockerState: "Ready To Exchange",
    applicableBSA: true,
  },

  // ── 11 Under Offer (progressing) ─────────────────────────────────
  //
  // iter-271 — Only the 4 most-advanced Under-Offer deals carry
  // exchange dates inside the 6-month forecast horizon (May-Oct
  // 2026). The remaining 7 sit beyond Oct so the forecast totals
  // reconcile to the underlying transactions without
  // double-counting future stock.
  {
    id: "p-stratford", dealId: _dealUuid("stratford"), dealSlug: "deal-stratford",
    image: HOUSE("photo-1493809842364-78817add7ffb"),
    address: "Apt 1106, Stratford One",
    postcode: "E15 1AZ",
    locality: "Stratford, London",
    value: 540000, commissionRate: 0.02,
    beds: 2, baths: 2, parking: "Allocated · 1",
    tenure: "leasehold",
    agent: "Oliver Carter",
    status: "Under Offer", risk: "low",
    exchangeDate: "2026-06-18", completionDate: "2026-07-09",
    stage: "exchange_ready",
    currentMilestone: "Mortgage",
    confidence: 80,
    progressNote: "Mortgage offer received · contracts approved",
    blockerState: "Ready To Exchange",
    applicableBSA: true,
  },
  {
    id: "p-lee", dealId: _dealUuid("lee"), dealSlug: "deal-lee",
    image: HOUSE("photo-1572120360610-d971b9d7767c"),
    address: "47 Manor Lane",
    postcode: "SE12 8LR",
    locality: "Lee, London",
    value: 695000, commissionRate: 0.0195,
    beds: 3, baths: 2, parking: "On-street",
    tenure: "leasehold",
    agent: "Rhys Drewell",
    status: "Under Offer", risk: "low",
    exchangeDate: "2026-08-20", completionDate: "2026-09-10",
    stage: "mortgage_offer",
    currentMilestone: "Mortgage",
    confidence: 76,
    progressNote: "Mortgage offer issued · contracts pending",
    blockerState: "Mortgage Valuation Pending",
  },
  {
    id: "p-islington", dealId: _dealUuid("islington"), dealSlug: "deal-islington",
    image: HOUSE("photo-1576941089067-2de3c901e126"),
    address: "17 Cloudesley Square",
    postcode: "N1 0HS",
    locality: "Islington, London",
    value: 1475000, commissionRate: 0.013,
    beds: 3, baths: 2, parking: "On-street",
    tenure: "freehold",
    agent: "Sophie Bennett",
    status: "Under Offer", risk: "low",
    exchangeDate: "2026-08-06", completionDate: "2026-08-27",
    stage: "searches",
    currentMilestone: "Searches",
    confidence: 84,
    progressNote: "Local authority searches lodged with Islington on 12 Jun · returns due Thu · buyer solicitor already drafted enquiries so contracts can go out same day",
    blockerState: "Searches Underway",
    // iter-382 · Stage-2 supporting deal · Straightforward freehold arc.
    // • Buyer Solicitor: Langley Curran (Nathaniel Langley) —
    //   pragmatic, sends enquiries with the search pack so the
    //   deal doesn't stall waiting on returns.
    // • Seller Solicitor: Pendrick Law (Ophelia Pendrick).
    // • Mortgage Broker: Northbridge Capital (Adaeze Okoye) —
    //   valuation returned above asking on 4 Jun, no down-valuation.
    // • Complication: none — this is the "everything works" pilot.
    pilotSupporting: true,
    supportingRole: "freehold-straightforward",
    supportingBuyer:  "Dr & Dr Aslam (NatWest mortgage · valuation clean)",
    supportingSeller: "Mr Julian Herbert-Wray (moving to Bath · onward freehold, chain-free)",
    supportingCurrentAction: "Chase Islington LB search return by 25 Jun · draft contracts ready to fire same day",
  },
  {
    id: "p-chingford", dealId: _dealUuid("chingford"), dealSlug: "deal-chingford",
    image: HOUSE("photo-1564013799919-ab600027ffc6"),
    address: "32 Endlebury Road",
    postcode: "E4 6QA",
    locality: "Chingford, London",
    value: 615000, commissionRate: 0.02,
    beds: 3, baths: 1, parking: "Driveway · 2",
    tenure: "leasehold",
    agent: "James Walker",
    status: "Under Offer", risk: "medium",
    exchangeDate: "2026-06-25", completionDate: "2026-07-16",
    stage: "searches",
    currentMilestone: "Searches",
    confidence: 65,
    progressNote: "Searches returned · enquiries cleared",
    blockerState: "Searches Delayed",
  },
  {
    id: "p-walthamstow", dealId: _dealUuid("walthamstow"), dealSlug: "deal-walthamstow",
    image: HOUSE("photo-1605146769289-440113cc3d00"),
    address: "11 Howard Road",
    postcode: "E17 4SH",
    locality: "Walthamstow, London",
    value: 725000, commissionRate: 0.0195,
    beds: 3, baths: 1, parking: "Residents permit",
    tenure: "leasehold",
    agent: "Rhys Drewell",
    status: "Under Offer", risk: "medium",
    exchangeDate: "2026-09-17", completionDate: "2026-10-08",
    stage: "survey",
    currentMilestone: "Survey",
    confidence: 68,
    progressNote: "Survey queries raised · awaiting seller reply",
    blockerState: "Survey Queries Raised",
  },
  {
    id: "p-chelsea", dealId: _dealUuid("chelsea"), dealSlug: "deal-chelsea",
    image: HOUSE("photo-1600585154340-be6161a56a0c"),
    address: "8 Sloane Avenue",
    postcode: "SW3 3JG",
    locality: "Chelsea, London",
    value: 1400000, commissionRate: 0.0095,
    beds: 3, baths: 2, parking: "Garage · 1",
    tenure: "leasehold",
    agent: "Sophie Bennett",
    status: "Under Offer", risk: "low",
    exchangeDate: "2026-09-10", completionDate: "2026-10-01",
    stage: "survey",
    currentMilestone: "Survey",
    confidence: 78,
    progressNote: "Structural survey scheduled",
    blockerState: "Survey Queries Raised",
    financeType: "cash",
  },
  {
    id: "p-canonbury", dealId: _dealUuid("canonbury"), dealSlug: "deal-canonbury",
    image: HOUSE("photo-1600047509807-ba8f99d2cdde"),
    address: "224 Canonbury Park",
    postcode: "N1 2JT",
    locality: "Islington, London",
    value: 1895000, commissionRate: 0.006,
    beds: 5, baths: 3, parking: "Driveway · 2",
    tenure: "freehold",
    agent: "Oliver Carter",
    status: "Under Offer", risk: "low",
    exchangeDate: "2026-09-24", completionDate: "2026-10-15",
    stage: "drafts",
    currentMilestone: "Draft Contract",
    confidence: 82,
    progressNote: "Cash buyer · proof of funds on file · Draft contract pack issued · Enquiries returned within 48h",
    blockerState: "Enquiries Raised",
    financeType: "cash",
    chainFree: true,
    // iter-382 · Stage-2 supporting deal · Cash-buyer, chain-free arc.
    // • Buyer: Ms Constance Ridley — cash offer at £1.895M,
    //   proof of funds verified by buyer solicitor 12 May.
    // • Buyer Solicitor: Nightingale Legal (Meredith Nightingale)
    //   — cash-buyer specialist, no mortgage / lender lag.
    // • Seller Solicitor: Halstead & Rowe (Callum Halstead).
    // • No mortgage broker on this transaction — the ChainFlow
    //   Deal Room correctly hides the Mortgage milestone.
    pilotSupporting: true,
    supportingRole: "cash-buyer-chain-free",
    supportingBuyer:  "Ms Constance Ridley (cash · proof of funds on file · no mortgage lag)",
    supportingSeller: "Prof. & Mrs Whitfield-Brown (downsizing to Frome · onward purchase chain-free)",
    supportingCurrentAction: "Chase Halstead & Rowe on the last two enquiries · targeting draft contract sign-off by 26 Jun",
  },
  {
    id: "p-wapping", dealId: _dealUuid("wapping"), dealSlug: "deal-wapping",
    image: HOUSE("photo-1502672260266-1c1ef2d93688"),
    address: "Apt 504, Cinnabar Wharf",
    postcode: "E1W 1JL",
    locality: "Wapping, London",
    value: 825000, commissionRate: 0.015,
    beds: 2, baths: 2, parking: "Underground · 1",
    tenure: "leasehold",
    agent: "James Walker",
    status: "Under Offer", risk: "low",
    exchangeDate: "2026-07-23", completionDate: "2026-08-13",
    stage: "drafts",
    currentMilestone: "Draft Contract",
    confidence: 80,
    progressNote: "Riverside dev · draft pack issued",
    blockerState: "Contracts Issued",
    applicableBSA: true,
  },
  {
    id: "p-ontario", dealId: _dealUuid("ontario"), dealSlug: "deal-ontario",
    image: HOUSE("photo-1486718448742-163732cd1544"),
    address: "Apt 312, Ontario Tower",
    postcode: "E14 9LU",
    locality: "Canary Wharf, London",
    value: 620000, commissionRate: 0.02,
    beds: 1, baths: 1, parking: "Allocated · 1",
    tenure: "leasehold",
    agent: "Rhys Drewell",
    status: "Under Offer", risk: "low",
    exchangeDate: "2026-07-16", completionDate: "2026-08-06",
    stage: "drafts",
    currentMilestone: "AML",
    confidence: 72,
    progressNote: "Contracts under review · AML cleared",
    blockerState: "Enquiries Raised",
    applicableBSA: true,
  },
  // iter-271 — Kensington (new) · Under Offer · leasehold · exchange Oct.
  {
    id: "p-kensington", dealId: _dealUuid("kensington"), dealSlug: "deal-kensington",
    image: HOUSE("photo-1600585154340-be6161a56a0c"),
    address: "12 Stanhope Gardens",
    postcode: "SW7 5RB",
    locality: "Kensington, London",
    value: 1400000, commissionRate: 0.013,
    beds: 3, baths: 2, parking: "Residents permit",
    tenure: "leasehold",
    agent: "Sophie Bennett",
    status: "Under Offer", risk: "low",
    exchangeDate: "2026-10-08", completionDate: "2026-10-29",
    stage: "drafts",
    currentMilestone: "Draft Contract",
    confidence: 82,
    progressNote: "Period mansion flat · contracts pending",
    blockerState: "Contracts Issued",
  },
  // iter-271 — Highgate (new) · Under Offer · leasehold · out-of-horizon.
  {
    id: "p-highgate", dealId: _dealUuid("highgate"), dealSlug: "deal-highgate",
    image: HOUSE("photo-1600585154526-990dced4db0d"),
    address: "44 Highgate West Hill",
    postcode: "N6 6NJ",
    locality: "Highgate, London",
    value: 685000, commissionRate: 0.0195,
    beds: 2, baths: 1, parking: "On-street",
    tenure: "leasehold",
    agent: "James Walker",
    status: "Under Offer", risk: "low",
    exchangeDate: "2026-08-13", completionDate: "2026-09-03",
    stage: "searches",
    currentMilestone: "Searches",
    confidence: 73,
    progressNote: "Searches in progress · returns expected Mon",
    blockerState: "Searches Underway",
  },

  // ── 5 At Risk (blocker / chain scenarios) ────────────────────────
  //
  // iter-271 — At Risk deals carry exchange dates beyond the
  // 6-month forecast horizon. They surface in the Forecasting
  // Centre as "Need Attention" (low confidence) and feed the
  // Operational Pressure rail, but their fees aren't booked into
  // the forecast revenue total until the blocker is resolved and
  // the date is pulled back in.
  {
    id: "p-canary", dealId: _dealUuid("canary"), dealSlug: "deal-canary",
    image: HOUSE("photo-1545324418-cc1a3fa10c00"),
    address: "Apt 2208, Pan Peninsula",
    postcode: "E14 9JT",
    locality: "Canary Wharf, London",
    value: 925000, commissionRate: 0.013,
    beds: 2, baths: 2, parking: "Underground · 1",
    tenure: "leasehold",
    agent: "James Walker",
    status: "At Risk", risk: "high",
    exchangeDate: "2026-11-12", completionDate: "2026-12-03",
    stage: "mgmt_pack",
    currentMilestone: "Mgmt Pack",
    confidence: 54,
    progressNote: "EWS1 received · contracts under review",
    blockerState: "Management Pack Outstanding",
    applicableBSA: true,
  },
  {
    id: "p-dartford", dealId: _dealUuid("dartford"), dealSlug: "deal-dartford",
    image: HOUSE("photo-1600585154526-990dced4db0d"),
    address: "8 Birchwood Park Avenue",
    postcode: "DA3 8AT",
    locality: "Dartford, Kent",
    value: 565000, commissionRate: 0.02,
    beds: 4, baths: 2, parking: "Garage · 2",
    tenure: "freehold",
    agent: "Oliver Carter",
    status: "At Risk", risk: "high",
    exchangeDate: "2026-11-19", completionDate: "2026-12-10",
    stage: "mortgage_app",
    currentMilestone: "Mortgage",
    confidence: 48,
    progressNote: "Chain rebuilt · mortgage offer received",
    blockerState: "Chain Unconfirmed",
  },
  {
    id: "p-limehouse", dealId: _dealUuid("limehouse"), dealSlug: "deal-limehouse",
    image: HOUSE("photo-1494526585095-c41746248156"),
    address: "Apt 84, Galleon House",
    postcode: "E14 8HF",
    locality: "Limehouse, London",
    value: 585000, commissionRate: 0.02,
    beds: 1, baths: 1, parking: "Allocated · 1",
    tenure: "leasehold",
    agent: "Sophie Bennett",
    status: "At Risk", risk: "high",
    exchangeDate: "2026-09-28", completionDate: "2026-10-19",
    stage: "survey",
    currentMilestone: "Survey",
    confidence: 52,
    progressNote: "Damp queries resolved · contracts under review",
    blockerState: "Survey Queries Raised",
    applicableBSA: true,
  },
  {
    id: "p-blackheath", dealId: _dealUuid("blackheath"), dealSlug: "deal-blackheath",
    image: HOUSE("photo-1599809275671-b5942cabc7a2"),
    address: "41 Bennett Park",
    postcode: "SE3 9RB",
    locality: "Blackheath, London",
    value: 1150000, commissionRate: 0.015,
    beds: 4, baths: 2, parking: "Driveway · 2",
    tenure: "freehold",
    agent: "Rhys Drewell",
    status: "At Risk", risk: "medium",
    exchangeDate: "2026-10-22", completionDate: "2026-11-12",
    stage: "searches",
    currentMilestone: "Searches",
    confidence: 60,
    progressNote: "Searches returned · council confirmed",
    blockerState: "Searches Delayed",
  },
  // iter-271 — Hampstead (new) · At Risk · freehold · chain risk.
  {
    id: "p-hampstead", dealId: _dealUuid("hampstead"), dealSlug: "deal-hampstead",
    image: HOUSE("photo-1600047509807-ba8f99d2cdde"),
    address: "9 Frognal Way",
    postcode: "NW3 6XE",
    locality: "Hampstead, London",
    value: 2100000, commissionRate: 0.01,
    beds: 5, baths: 3, parking: "Driveway · 2",
    tenure: "freehold",
    agent: "Rhys Drewell",
    status: "At Risk", risk: "medium",
    exchangeDate: "2026-11-05", completionDate: "2026-11-26",
    stage: "survey",
    currentMilestone: "Survey",
    confidence: 56,
    progressNote: "Retention agreed · contracts being finalised",
    blockerState: "Survey Queries Raised",
  },
  // iter-376 — Sample Offer deal (fresh offer-stage transaction).
  // Purpose: gives pilot operators a live-cascade demo target for
  // "Mark Offer Agreed → Transaction Starts". Chain + mortgage
  // buyer so the cascade lands on the Mortgage milestone
  // (matching the majority of real-world flows). No blocker,
  // freehold, mid-market price so the deal reads as an ordinary
  // agreed offer awaiting the operator's confirm.
  {
    id: "p-sample-offer", dealId: _dealUuid("sample-offer"), dealSlug: "deal-sample-offer",
    image: HOUSE("photo-1560448204-e02f11c3d0e2"),
    address: "42 Barnsbury Square",
    postcode: "N1 1JL",
    locality: "Barnsbury, London",
    value: 895000, commissionRate: 0.01,
    beds: 3, baths: 2, parking: "Residents permit",
    tenure: "freehold",
    agent: "Rhys Drewell",
    status: "Under Offer", risk: "low",
    exchangeDate: "2026-12-11", completionDate: "2027-01-08",
    stage: "offer",
    currentMilestone: "Offer Agreed",
    confidence: 68,
    progressNote: "Offer accepted · awaiting Memorandum of Sale",
    blockerState: null,
  },
];

/* Compute progressPercent + journey + fee for every property
 * exactly once at module load. Consumers never re-derive. */
export const PILOT_PROPERTIES = Object.freeze(
  _PILOT_BASE.map((p) =>
    Object.freeze({
      ...p,
      fee: feeFor(p),
      commissionRate: _rateFor(p),
      journey: journeyFor(p.tenure),
      progressPercent: progressFor(p.tenure, p.currentMilestone),
    })
  )
);

/* ─── Shared Professional Network (iter-380 · Stage 1) ────────────
 *
 * A single reusable pool of realistic UK conveyancing professionals
 * that recurs across every pilot deal. Every persona is
 * deterministically picked per `deal.id` via `_participantsFor()` so
 * the same faces surface across the portfolio — mirroring how a real
 * London estate agency's preferred panel operates. Names and firms
 * are believable but wholly fictional; no real practising firm is
 * impersonated.
 *
 * Roster:
 *   • Buyer Solicitors     — 8 firms (Holloway & Co, Fielding
 *                            Westgate, Westgate Conveyancing,
 *                            Ashcroft Lyle, Langley Curran,
 *                            Kestrel & Ward, Nightingale Legal,
 *                            Oswald Bramwell)
 *   • Seller Solicitors    — 8 firms (Bowden Holroyd, Marling &
 *                            Partners, Pendrick Law, Crawford
 *                            Beddows, Tremayne Cross, Halstead &
 *                            Fell, Verity Rowntree, Inglewood
 *                            Chambers)
 *   • Mortgage Brokers     — 6 desks (TMW Mortgages, Northbridge
 *                            Capital, Farrell Ashfield, Hendricks
 *                            Private Finance, Santoro & Blake,
 *                            Linden Wharf)
 *   • Chartered Surveyors  — 8 practices (Ashworth Surveys, Orchard
 *                            Chartered Surveyors, Quinn & Sallow,
 *                            Tallis Property Surveys, Copeland Rowe,
 *                            Mercer Riddell, Baxter Cheswold,
 *                            Wexford Surveyors LLP)
 *   • Managing Agents      — 8 block-management firms (Tannery Row
 *                            Estates, Canonbury Management, Parkway
 *                            Residents, Birchwood Block Management,
 *                            Heron Court Managing, Ludgate & Sons,
 *                            Thornbury Estates, Camden Court
 *                            Managing) — attached only to leasehold
 *                            deals.
 *   • Lead Agents          — 4 (Rhys Drewell, Sophie Bennett, Oliver
 *                            Carter, James Walker) — set per property.
 *   • Assistant Negotiators — 5 (ChainFlow Pilot Office).
 *   • Buyer & seller pools — 24 each, drawn from believable British
 *                            surnames + salutations (Mr/Mrs/Ms/Dr).
 *
 * Contact details:
 *   • Phones use Ofcom's drama-reserved `+44 7700 9XX XXX` range so
 *     no real subscriber can be dialled.
 *   • Emails use the firm slug (e.g. `bowdenholroyd.co.uk`) or the
 *     `demo.chainflow.app` sink for buyers/sellers.
 *
 * Stage 2 will extend the same panel across ALL remaining pilot
 * deals and layer richer document / milestone history on each.
 * ─────────────────────────────────────────────────────────────── */
const _PERSONA_POOL = {
  buyerSolicitors:  [
    { id: "ps-holloway",  name: "Sarah Holloway",   firm: "Holloway & Co" },
    { id: "ps-fielding",  name: "James Fielding",   firm: "Fielding Westgate" },
    { id: "ps-westgate",  name: "Helena Westgate",  firm: "Westgate Conveyancing" },
    { id: "ps-ashcroft",  name: "Priya Ashcroft",   firm: "Ashcroft Lyle" },
    { id: "ps-langley",   name: "Elliot Langley",   firm: "Langley Curran" },
    { id: "ps-kestrel",   name: "Marina Kestrel",   firm: "Kestrel & Ward" },
    { id: "ps-nightingale", name: "Rowan Nightingale", firm: "Nightingale Legal" },
    { id: "ps-oswald",    name: "Amelia Oswald",    firm: "Oswald Bramwell" },
  ],
  sellerSolicitors: [
    { id: "ss-bowden",    name: "Helena Bowden",    firm: "Bowden Holroyd" },
    { id: "ss-marling",   name: "Daniel Marling",   firm: "Marling & Partners" },
    { id: "ss-pendrick",  name: "Olivia Pendrick",  firm: "Pendrick Law" },
    { id: "ss-crawford",  name: "Nathaniel Crawford", firm: "Crawford Beddows" },
    { id: "ss-tremayne",  name: "Beatrice Tremayne", firm: "Tremayne Cross" },
    { id: "ss-halstead",  name: "Julian Halstead",  firm: "Halstead & Fell" },
    { id: "ss-verity",    name: "Rebecca Verity",   firm: "Verity Rowntree" },
    { id: "ss-inglewood", name: "Thomas Inglewood", firm: "Inglewood Chambers" },
  ],
  brokers: [
    { id: "br-park",      name: "Daniel Park",     firm: "TMW Mortgages" },
    { id: "br-okoye",     name: "Adaeze Okoye",    firm: "Northbridge Capital" },
    { id: "br-farrell",   name: "Ciara Farrell",   firm: "Farrell Ashfield" },
    { id: "br-hendricks", name: "Marcus Hendricks", firm: "Hendricks Private Finance" },
    { id: "br-santoro",   name: "Lorenzo Santoro", firm: "Santoro & Blake" },
    { id: "br-linden",    name: "Georgia Linden",  firm: "Linden Wharf" },
  ],
  surveyors: [
    { id: "sv-ashworth",  name: "David Ashworth",   firm: "Ashworth Surveys" },
    { id: "sv-orchard",   name: "Emily Orchard",    firm: "Orchard Chartered Surveyors" },
    { id: "sv-quinn",     name: "Patrick Quinn",    firm: "Quinn & Sallow" },
    { id: "sv-tallis",    name: "Miriam Tallis",    firm: "Tallis Property Surveys" },
    { id: "sv-copeland",  name: "Adrian Copeland",  firm: "Copeland Rowe" },
    { id: "sv-mercer",    name: "Sofia Mercer",     firm: "Mercer Riddell" },
    { id: "sv-baxter",    name: "Nigel Baxter",     firm: "Baxter Cheswold" },
    { id: "sv-wexford",   name: "Louise Wexford",   firm: "Wexford Surveyors LLP" },
  ],
  managingAgents: [
    { id: "ma-tannery",   name: "Ravi Menon",       firm: "Tannery Row Estates" },
    { id: "ma-canonbury", name: "Yasmin Al-Rashid", firm: "Canonbury Management" },
    { id: "ma-parkway",   name: "Colin Yeatman",    firm: "Parkway Residents" },
    { id: "ma-birchwood", name: "Fiona Delahunt",   firm: "Birchwood Block Management" },
    { id: "ma-heron",     name: "Grace Chamberlain", firm: "Heron Court Managing" },
    { id: "ma-ludgate",   name: "Hugo Whitaker",    firm: "Ludgate & Sons" },
    { id: "ma-thornbury", name: "Nadia Sharpe",     firm: "Thornbury Estates" },
    { id: "ma-camden",    name: "Robert Faulkner",  firm: "Camden Court Managing" },
  ],
  estateAgentAssistants: [
    // Assistant negotiators — supporting the four Lead Agents.
    { id: "ea-1", name: "Isabelle Marlowe",   firm: "ChainFlow Pilot Office" },
    { id: "ea-2", name: "Freddie Sinclair",   firm: "ChainFlow Pilot Office" },
    { id: "ea-3", name: "Priti Ramamurthy",   firm: "ChainFlow Pilot Office" },
    { id: "ea-4", name: "Anders Bergstrom",   firm: "ChainFlow Pilot Office" },
    { id: "ea-5", name: "Chloe Fitzgerald",   firm: "ChainFlow Pilot Office" },
  ],
  buyers: [
    "Mr & Mrs Sutherland", "Ms Priya Iqbal", "Mr Henry Lockhart",
    "Mr & Mrs Chen", "Ms Imogen Hartfield", "Dr Aaron Vidal",
    "Mr Marcus Whitley", "Mr & Mrs Beaumont", "Ms Charlotte Reeve",
    "Mr Liam Easton", "Mr & Mrs Olafsson", "Ms Sienna Patel",
    "Dr Edward Pengelly", "Mr & Mrs Caldwell", "Mr Felix Mendelson",
    "Mr Owen Strathmore", "Ms Esther Lindgren", "Mr & Mrs Trevelyan",
    "Ms Vera Konstantinov", "Mr Rajeev Bhatt", "Dr Yuki Hasegawa",
    "Mr & Mrs Ó Cathasaigh", "Ms Lauren Chetwynd", "Mr Nikolai Volkov",
  ],
  sellers: [
    "Ms Naomi Ellsworth", "Mr & Mrs Whittaker", "Mrs Catherine Halligan",
    "Mr Peter Drysdale", "Mr & Mrs Acheson", "Mr Edward Pickford",
    "Mrs Anna Schofield", "Mr & Mrs Brierley", "Ms Isabel Markovic",
    "Mr Roland Tate", "Mrs Jocelyn Frye", "Mr & Mrs Gladwin",
    "Mr Robert Stanhope", "Ms Tamsin Holloway", "Mr & Mrs Riddell",
    "Dr Mira Anand", "Mr Lawrence Fenwick", "Mrs Caroline Vale",
    "Dr Kwame Osei-Bonsu", "Ms Rowena Blythe", "Mr & Mrs Zieliński",
    "Mrs Harriet Fairfax", "Mr Cormac Doherty", "Ms Anaïs Rousseau",
  ],
};

const _hashIndex = (s, n) => {
  // Deterministic index from a string — keeps seeds stable so tests
  // and screenshots produce identical output.
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % n;
};

const _personaFor = (dealId, pool) => pool[_hashIndex(dealId, pool.length)];

/* iter-295 — Per-participant deterministic contact details.
 *
 * The Deal Room rail surfaces phone / message / email quick-
 * actions for every participant. Without contact data those icons
 * collapse into a disabled grey state, making the rail read as
 * static UI. We backfill realistic-looking British contact data
 * derived from `participant.id` so:
 *
 *   • the same participant carries the same details across reloads;
 *   • no real-world person is impersonated (numbers are inside
 *     Ofcom's `+44 7700 9XXXXX` reserved range for drama / demos);
 *   • the email aligns with the participant's role + organisation
 *     so operators can see at a glance who the row routes to.
 */
const _hashCode = (str) => {
  let h = 0;
  const s = String(str || "");
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};
const _slugForEmail = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/[^a-z]+/g, ".")
    .replace(/^\.+|\.+$/g, "")
    .slice(0, 24) || "contact";
const _firmDomain = (organisation) => {
  if (!organisation) return null;
  const slug = String(organisation).toLowerCase().replace(/[^a-z]+/g, "");
  return slug ? `${slug.slice(0, 20)}.co.uk` : null;
};
const _phoneFor = (id) => {
  // Ofcom-reserved drama range: +44 7700 9XX XXX → never collides with a real subscriber.
  const seed = _hashCode(id);
  const five = String(seed % 100000).padStart(5, "0");
  return `+44 7700 9${five.slice(0, 2)} ${five.slice(2)}`;
};
const _emailFor = (name, organisation, role) => {
  const local = _slugForEmail(name);
  const domain = _firmDomain(organisation)
    || (role === "Buyer" || role === "Seller" ? "demo.chainflow.app" : "demo.chainflow.app");
  return `${local}@${domain}`;
};

/* ─── Per-deal participant roster ──────────────────────────────── */
const _participantsFor = (p) => {
  const buyer  = _PERSONA_POOL.buyers[_hashIndex(p.dealId + "::b", _PERSONA_POOL.buyers.length)];
  const seller = _PERSONA_POOL.sellers[_hashIndex(p.dealId + "::s", _PERSONA_POOL.sellers.length)];
  const bsol   = _personaFor(p.dealId + "::bsol", _PERSONA_POOL.buyerSolicitors);
  const ssol   = _personaFor(p.dealId + "::ssol", _PERSONA_POOL.sellerSolicitors);
  const brk    = _personaFor(p.dealId + "::brk",  _PERSONA_POOL.brokers);
  const svy    = _personaFor(p.dealId + "::svy",  _PERSONA_POOL.surveyors);
  const asst   = _personaFor(p.dealId + "::asst", _PERSONA_POOL.estateAgentAssistants);
  // iter-366B — Managing Agent only applies to leasehold deals.
  const mgm    = p.tenure === "leasehold"
    ? _personaFor(p.dealId + "::mgm", _PERSONA_POOL.managingAgents)
    : null;
  const seed   = (suffix) => `${p.dealSlug || p.dealId}-${suffix}`;
  const _withContact = (id, role, name, organisation) => ({
    id,
    role,
    name,
    organisation,
    phone: _phoneFor(id),
    email: _emailFor(name, organisation, role),
  });
  const roster = [
    _withContact(seed("buyer"),  "Buyer",            buyer,      null),
    _withContact(seed("seller"), "Seller",           seller,     null),
    _withContact(seed("bsol"),   "Buyer Solicitor",  bsol.name,  bsol.firm),
    _withContact(seed("ssol"),   "Seller Solicitor", ssol.name,  ssol.firm),
    _withContact(seed("broker"), "Mortgage Broker",  brk.name,   brk.firm),
    _withContact(seed("agent"),  "Estate Agent",     p.agent,    "ChainFlow Pilot Office"),
    _withContact(seed("asst"),   "Assistant Agent",  asst.name,  asst.firm),
    _withContact(seed("surveyor"), "Surveyor",       svy.name,   svy.firm),
  ];
  if (mgm) {
    roster.push(_withContact(seed("mgm"), "Managing Agent", mgm.name, mgm.firm));
  }
  return roster;
};

/* ─── Per-deal document roster (stage-appropriate) ────────────── */
const _MILESTONE_RANK = {
  Offer: 0, AML: 1, Survey: 2, "Draft Contract": 3, "Mgmt Pack": 4,
  Searches: 5, Mortgage: 6, Exchange: 7, Completion: 8,
};
// For freehold (no Mgmt Pack), shift: Searches=4, Mortgage=5, Exchange=6
const _milestoneRank = (tenure, milestone) => {
  if (tenure === "freehold" && _MILESTONE_RANK[milestone] >= 5) {
    return _MILESTONE_RANK[milestone] - 1;
  }
  return _MILESTONE_RANK[milestone] ?? 0;
};

const _isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
};

const _DOCS_BY_RANK = [
  // rank, label, status, uploaderRole, daysAgo
  [0, "Memorandum of Sale.pdf",      "Approved",  "Estate Agent",     45],
  [1, "Buyer ID & Proof of Address.pdf", "Approved", "Buyer Solicitor", 38],
  [2, "Survey Report.pdf",            "Received",  "Buyer",            30],
  [3, "Draft Contract Pack.pdf",      "Received",  "Seller Solicitor", 24],
  [4, "Management Pack.pdf",          "Pending",   "Seller Solicitor", 18],
  [5, "Local Authority Searches.pdf", "Received",  "Buyer Solicitor",  14],
  [6, "Mortgage Offer.pdf",           "Approved",  "Mortgage Broker",  7],
];

const _documentsFor = (p) => {
  const maxRank = _milestoneRank(p.tenure, p.currentMilestone);
  const docs = [];
  for (const [rank, label, status, uploader, daysAgo] of _DOCS_BY_RANK) {
    // Skip Management Pack on freehold deals.
    if (label === "Management Pack.pdf" && p.tenure === "freehold") continue;
    if (rank > maxRank) continue;
    // Stagger: At Risk deals' current-milestone docs sit as Pending.
    const isCurrent = rank === maxRank;
    const isBlockedDoc =
      p.status === "At Risk" && isCurrent &&
      ((label.includes("Management Pack") && p.blockerState === "Management Pack Outstanding") ||
       (label.includes("Searches")        && p.blockerState === "Searches Delayed") ||
       (label.includes("Survey")          && p.blockerState === "Survey Queries Raised"));
    const finalStatus = isBlockedDoc ? "Pending" : status;
    docs.push({
      id: `${p.dealSlug || p.dealId}-doc-${rank}`,
      name: label,
      title: label,
      status: finalStatus,
      uploaded_by_name: `${uploader} · ${p.agent}`.split(" · ")[0],
      created_at: _isoDaysAgo(daysAgo + _hashIndex(p.dealId + label, 4)),
      milestone: Object.keys(_MILESTONE_RANK).find((k) => _MILESTONE_RANK[k] === rank) || null,
    });
  }
  return docs;
};

/* ─── Per-deal activity history (stage + blocker aware) ──────── */
const _ACTIVITY_BY_RANK = [
  [0, "Memorandum of Sale issued",       "Estate Agent",      48],
  [1, "AML / ID checks completed",       "Buyer Solicitor",   40],
  [2, "Survey booked",                   "Buyer",             34],
  [2, "Survey report received",          "Buyer",             29],
  [3, "Draft contract pack issued",      "Seller Solicitor",  25],
  [3, "Enquiries raised",                "Buyer Solicitor",   20],
  [4, "Management pack requested",       "Seller Solicitor",  18],
  [4, "Management pack outstanding",     "Seller Solicitor",  11],
  [5, "Searches requested",              "Buyer Solicitor",   16],
  [5, "Local authority searches returned","Buyer Solicitor",   9],
  [6, "Mortgage valuation booked",       "Mortgage Broker",   13],
  [6, "Mortgage offer received",         "Mortgage Broker",   6],
  [7, "Exchange of contracts confirmed", "Buyer Solicitor",   2],
];

const _BLOCKER_ACTIVITY = {
  "Management Pack Outstanding":  "Management pack 11d overdue · escalated to managing agent",
  "Searches Delayed":             "Local authority searches overdue · chased council",
  "Searches Underway":            "Searches in progress · returns expected end of week",
  "Mortgage Valuation Pending":   "Mortgage valuation booked · awaiting surveyor",
  "Survey Queries Raised":        "Survey queries raised · awaiting seller response",
  "Chain Unconfirmed":            "Chain above collapsed · seeking alternative buyers",
  "Enquiries Raised":             "Enquiries raised on draft contract · response due",
  "Contracts Issued":             "Draft contract pack issued · enquiries returned",
  "Ready To Exchange":            "Contracts signed · exchange imminent",
  "Cash Buyer Progressing":       "Cash buyer · proof of funds verified",
};

const _activityFor = (p) => {
  const maxRank = _milestoneRank(p.tenure, p.currentMilestone);
  const events = [];
  for (const [rank, label, actorRole, daysAgo] of _ACTIVITY_BY_RANK) {
    if (rank > maxRank) continue;
    // Skip Management Pack activity on freehold.
    if (label.toLowerCase().includes("management pack") && p.tenure === "freehold") continue;
    events.push({
      id: `${p.dealSlug || p.dealId}-act-${events.length}`,
      type: rank === maxRank ? "milestone" : "event",
      label,
      actor_role: actorRole,
      created_at: _isoDaysAgo(daysAgo + _hashIndex(p.dealId + label, 3)),
    });
  }
  // Pin the live blocker / momentum line on top.
  const headline = _BLOCKER_ACTIVITY[p.blockerState];
  if (headline) {
    events.push({
      id: `${p.dealSlug || p.dealId}-act-headline`,
      type: p.status === "At Risk" ? "blocker" : "update",
      label: headline,
      actor_role: "Estate Agent",
      created_at: _isoDaysAgo(1),
    });
  }
  // Most recent first.
  events.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  return events;
};

/* ─── Derive canonical deals from canonical properties ─────────── */
const _STATUS_TO_DEAL = {
  "Sales Agreed": "Active",
  "Under Offer":  "Active",
  "At Risk":      "At Risk",
};

const _fmtMoney = (n) => `£${n.toLocaleString("en-GB")}`;

export const PILOT_DEALS = Object.freeze(
  PILOT_PROPERTIES.map((p) => {
    const participants = _participantsFor(p);
    const documents    = _documentsFor(p);
    const activity     = _activityFor(p);
    return Object.freeze({
      id: p.dealId,
      slug: p.dealSlug,
      propertyId: p.id,
      name: p.address,
      stage: p.stage,
      status: _STATUS_TO_DEAL[p.status] || "Active",
      risk: p.risk === "low" ? "Low" : p.risk === "medium" ? "Medium" : "High",
      agreedPrice: _fmtMoney(p.value),
      agreedPriceAmount: p.value,
      fee: p.fee,
      feePct: (p.commissionRate || PILOT_COMMISSION_RATE) * 100,
      commissionRate: p.commissionRate || PILOT_COMMISSION_RATE,
      vatIncluded: false,
      revenueAtRisk: p.risk === "low" ? "Low" : p.risk === "medium" ? "Medium" : "High",
      targetExchange: p.exchangeDate,
      targetCompletion: p.completionDate,
      expectedExchangeMonth: p.exchangeDate,
      progress: p.progressPercent,
      tenure: p.tenure,
      applicableBSA: p.applicableBSA === undefined ? undefined : !!p.applicableBSA,
      financeType: p.financeType || "mortgage",
      chainHealth: p.confidence,
      exchangeProbability: p.confidence,
      dealHealthScore: p.confidence,
      blockerState: p.blockerState,
      currentMilestone: p.currentMilestone,
      invitations: {},
      participants,
      documents,
      activity,
      messages: [],
      buyerName:           participants.find((x) => x.role === "Buyer")?.name || null,
      sellerName:          participants.find((x) => x.role === "Seller")?.name || null,
      buyerSolicitorName:  participants.find((x) => x.role === "Buyer Solicitor")?.name || null,
      sellerSolicitorName: participants.find((x) => x.role === "Seller Solicitor")?.name || null,
      brokerName:          participants.find((x) => x.role === "Mortgage Broker")?.name || null,
      agentName:           p.agent,
      favourite: false,
      _pilot: true,
    });
  })
);

/* ─── Properties shaped for chainStore consumers ───────────────── */
const _STATUS_TO_PROP = {
  "Sales Agreed": "Sales Agreed",
  "Under Offer":  "Under Offer",
  "At Risk":      "Under Offer",
};

export const PILOT_STORE_PROPERTIES = Object.freeze(
  PILOT_PROPERTIES.map((p) =>
    Object.freeze({
      id: p.id,
      address: `${p.address}, ${p.locality}`,
      postcode: p.postcode,
      type: p.tenure === "leasehold" ? "Apartment" : "House",
      beds: p.beds,
      baths: p.baths,
      price: _fmtMoney(p.value),
      priceAmount: p.value,
      tenure: p.tenure === "leasehold" ? "Leasehold" : "Freehold",
      epc: null,
      svc: null,
      rent: null,
      status: _STATUS_TO_PROP[p.status] || "Under Offer",
      sellerId: null,
      agentId: null,
      agent: p.agent,
      office: "London — Operational",
      thumb: p.image,
      notes: p.progressNote || "",
      parking: p.parking,
      parkingSpaces: null,
      createdAt: Date.now(),
      _pilot: true,
    })
  )
);

/* ─── Lookup maps ──────────────────────────────────────────────── */
export const PILOT_PROPERTY_BY_ID = Object.freeze(
  Object.fromEntries(PILOT_PROPERTIES.map((p) => [p.id, p]))
);
export const PILOT_DEAL_BY_ID = Object.freeze(
  Object.fromEntries(PILOT_DEALS.map((d) => [d.id, d]))
);
export const PILOT_DEAL_FOR_PROPERTY = Object.freeze(
  Object.fromEntries(PILOT_PROPERTIES.map((p) => [p.id, p.dealId]))
);

/* ─── Aggregate stats — used by Forecasting + regression tests ── */
export const PILOT_AGGREGATES = Object.freeze({
  totalProperties: PILOT_PROPERTIES.length,
  totalForecastRevenue: PILOT_PROPERTIES.reduce((s, p) => s + p.fee, 0),
  leaseholdCount: PILOT_PROPERTIES.filter((p) => p.tenure === "leasehold").length,
  freeholdCount: PILOT_PROPERTIES.filter((p) => p.tenure === "freehold").length,
  salesAgreedCount: PILOT_PROPERTIES.filter((p) => p.status === "Sales Agreed").length,
  underOfferCount:  PILOT_PROPERTIES.filter((p) => p.status === "Under Offer").length,
  atRiskCount:      PILOT_PROPERTIES.filter((p) => p.status === "At Risk").length,
});

/* ─── iter-270I · Unified People aggregation ──────────────────────
 *
 * Single source of truth for the People page (and any other relation-
 * ship surface). Derives one row per unique person (by name + role)
 * across every PILOT_DEAL's `participants` array, so the People
 * page reads the SAME solicitors / brokers / managing agent that
 * appear in Deal Rooms, the Documents Hub, and the Request a
 * Document recipient picker.
 *
 * Each aggregated row carries:
 *   • id            — deterministic slug
 *   • name, company, role, roleLabel
 *   • deals[]       — { dealId, prop, stage }
 *   • moreDeals     — count beyond the first 3 shown
 *   • ctxTitle      — "N active deals · M exchange ready"
 *   • ctxNote       — top blocker / momentum line across their deals
 *   • ctxTone       — amber / green / rose / neutral
 *   • ctxLast       — "Last active today / yesterday / Nd ago"
 *   • badge         — operational responsiveness tag
 *   • active        — bool
 */
const _DEAL_STAGE_LABEL = {
  offer: "Under Offer", aml: "Under Offer", memo: "Under Offer",
  solicitors: "Conveyancing", survey: "Conveyancing",
  drafts: "Conveyancing", "draft_contract": "Conveyancing",
  mgmt_pack: "Conveyancing", searches: "Conveyancing",
  enquiries: "Conveyancing", mortgage_app: "Conveyancing",
  mortgage_offer: "Pre-Exchange", exchange_ready: "Pre-Exchange",
  exchanged: "Exchanged", completed: "Exchanged",
};

const _ROLE_TO_GROUP = {
  "Buyer":            "buyer",
  "Seller":           "seller",
  "Buyer Solicitor":  "solicitor",
  "Seller Solicitor": "solicitor",
  "Mortgage Broker":  "broker",
  "Estate Agent":     "agent",
  "Assistant Agent":  "agent",
  "Surveyor":         "surveyor",
  "Managing Agent":   "managing_agent",
  "Past Client":      "past_client",
  "Prospect":         "prospect",
  "Referrer":         "referrer",
};

const _personSlug = (name) =>
  String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "person";


/* iter-366B — Extended CRM contacts.
 *
 * The People page must read as a live CRM that has been in use for
 * months. Beyond the 22 pilot deals' active participants, real
 * offices carry:
 *
 *   • Past clients — completed transactions, still receiving
 *     newsletters + referred deals.
 *   • Prospects   — registered buyers / sellers waiting for the
 *     right property.
 *   • Referrers   — sources of introductions (financial advisors,
 *     accountants, private client managers).
 *   • Chain contacts — people from linked upstream / downstream
 *     transactions the operator coordinates with.
 *
 * Each row references an existing pilot deal slug so the CRM stays
 * strictly consistent with the pilot data (no orphan contacts).
 * Roles fall outside the six live participant roles so the
 * aggregator can key them separately.
 *
 * Kept as plain seed data (no hash randomness) so screenshots +
 * tests stay stable across reloads. */
const _EXTENDED_CONTACTS = [
  // ── Past clients (completed transactions) ─────────────────────
  { role: "Past Client", name: "Mr & Mrs Hollingworth",  company: "Private",              dealSlug: "chelsea",    when: "3w ago",  via: "Email",     note: "Referred a friend · Kensington" },
  { role: "Past Client", name: "Ms Ekaterina Vasiliev",  company: "Private",              dealSlug: "battersea",  when: "1mo ago", via: "Christmas card", note: "Considering upsize · budget £2.4m" },
  { role: "Past Client", name: "Dr Rohan Chatterjee",    company: "Private",              dealSlug: "canonbury",  when: "6w ago",  via: "Phone",     note: "Warranty query · Camden flat" },
  { role: "Past Client", name: "Mr Silas Wren",          company: "Private",              dealSlug: "ontario",    when: "2mo ago", via: "Email",     note: "Positive review submitted" },
  { role: "Past Client", name: "Mrs Deirdre O'Halloran", company: "Private",              dealSlug: "holland-park", when: "5w ago", via: "WhatsApp", note: "Recommended us to sister" },
  { role: "Past Client", name: "Mr Cassian Rutherford",  company: "Private",              dealSlug: "highgate",   when: "8w ago",  via: "Email",     note: "Requested landlord references" },
  { role: "Past Client", name: "Ms Bianca Ferrari",      company: "Private",              dealSlug: "chingford",  when: "3mo ago", via: "Instagram", note: "Following new listings" },
  { role: "Past Client", name: "Mr & Mrs Yamaguchi",     company: "Private",              dealSlug: "limehouse",  when: "4mo ago", via: "Referral", note: "Referred colleague · Canary Wharf" },
  // ── Prospects (registered buyers / sellers) ───────────────────
  { role: "Prospect", name: "Ms Aleksandra Kowalski",    company: "Registered buyer",     dealSlug: "chelsea",    when: "2d ago",  via: "Property alert", note: "Interested in Chelsea Freehold · £1.8m" },
  { role: "Prospect", name: "Mr Terence Ballard",        company: "Registered buyer",     dealSlug: "battersea",  when: "5d ago",  via: "Viewing",   note: "Second viewing booked" },
  { role: "Prospect", name: "Dr Priyanka Nair",          company: "Registered buyer",     dealSlug: "canonbury",  when: "1w ago",  via: "Email",     note: "Cash proceedable · seeking 2-bed" },
  { role: "Prospect", name: "Mr Callum Trelawney",       company: "Registered buyer",     dealSlug: "holland-park", when: "2w ago", via: "Instagram", note: "Instagram enquiry" },
  { role: "Prospect", name: "Ms Solveig Andersen",       company: "Registered seller",    dealSlug: "highgate",   when: "3d ago",  via: "Valuation", note: "Considering listing · Highgate mansion" },
  { role: "Prospect", name: "Mr & Mrs Vasquez",          company: "Registered buyer",     dealSlug: "limehouse",  when: "1d ago",  via: "Portal",    note: "Rightmove enquiry · Wharf apartment" },
  { role: "Prospect", name: "Mr Jasper Cavendish",       company: "Registered seller",    dealSlug: "chingford",  when: "9d ago",  via: "Phone",     note: "Free market appraisal booked" },
  { role: "Prospect", name: "Ms Iolanthe Bright",        company: "Registered buyer",     dealSlug: "ontario",    when: "6d ago",  via: "Email",     note: "Under-offer buyer · downsizing" },
  { role: "Prospect", name: "Dr Malachi Osborne",        company: "Registered buyer",     dealSlug: "chelsea",    when: "11d ago", via: "Newsletter", note: "Waiting on chain confirmation" },
  { role: "Prospect", name: "Mrs Xiomara Delgado",       company: "Registered seller",    dealSlug: "battersea",  when: "4d ago",  via: "Email",     note: "Requested comparable sales report" },
  // ── Referrers (introductions) ─────────────────────────────────
  { role: "Referrer", name: "Mr Piers Bellingham",       company: "Bellingham Wealth",     dealSlug: "holland-park", when: "2d ago", via: "Phone",   note: "Referred 3 clients this quarter" },
  { role: "Referrer", name: "Ms Freya Sundqvist",        company: "Sundqvist Accountancy", dealSlug: "chelsea",    when: "1w ago",  via: "Email",     note: "Family office intro" },
  { role: "Referrer", name: "Mr Ashwin Ramaswamy",       company: "Ramaswamy Private Ltd", dealSlug: "battersea",  when: "3d ago",  via: "WhatsApp",  note: "High-net-worth buyer intro" },
  { role: "Referrer", name: "Mrs Antonia Petraccini",    company: "Petraccini Legal",      dealSlug: "canonbury",  when: "2w ago",  via: "Email",     note: "Divorce sale coordination" },
  { role: "Referrer", name: "Mr Rhodri Prendergast",     company: "Prendergast Advisors",  dealSlug: "highgate",   when: "1mo ago", via: "Phone",     note: "Pension release · downsize" },
  { role: "Referrer", name: "Ms Naledi Mokoena",         company: "Mokoena Family Office", dealSlug: "limehouse",  when: "5d ago",  via: "Email",     note: "Two overseas buyer intros" },
  { role: "Referrer", name: "Mr Tobias Fenchurch",       company: "Fenchurch Trust",       dealSlug: "ontario",    when: "2w ago",  via: "Meeting",   note: "Trust sale coordination" },
  { role: "Referrer", name: "Dr Elowen Trevaskis",       company: "Trevaskis Notaries",    dealSlug: "chingford",  when: "3w ago",  via: "Email",     note: "Probate valuation · notary" },
  // ── Chain contacts (linked upstream / downstream transactions) ─
  { role: "Past Client", name: "Mr Ivor Blackwood",      company: "Upward chain · SW3",   dealSlug: "chelsea",    when: "4d ago",  via: "Solicitor", note: "Their vendor's solicitor introduced" },
  { role: "Past Client", name: "Ms Sinead Lavelle",      company: "Downward chain · N6", dealSlug: "highgate",   when: "6d ago",  via: "Email",     note: "First-time buyer below" },
  { role: "Past Client", name: "Mr Kofi Amankwah",       company: "Upward chain · E14",  dealSlug: "limehouse",  when: "8d ago",  via: "Phone",     note: "Their seller confirmed exchange" },
  { role: "Past Client", name: "Mrs Nell Truscott",      company: "Downward chain · N1", dealSlug: "canonbury",  when: "3d ago",  via: "WhatsApp",  note: "Coordinating exchange dates" },
  { role: "Past Client", name: "Mr Ephraim Waldegrave",  company: "Upward chain · SW11", dealSlug: "battersea",  when: "12d ago", via: "Solicitor", note: "Their broker also using TMW" },
  { role: "Past Client", name: "Ms Yolanda Petit-Marchand", company: "Downward chain · E4", dealSlug: "chingford", when: "5d ago", via: "Email",   note: "Cash buyer · proceedable" },
  { role: "Past Client", name: "Dr Fionnuala Behan",     company: "Upward chain · W14",  dealSlug: "holland-park", when: "2w ago", via: "Meeting", note: "Buying Holland Park vendor's next home" },
  // ── Additional past clients (deeper history) ──────────────────
  { role: "Past Client", name: "Ms Winifred Ackerley",   company: "Private",              dealSlug: "chelsea",    when: "6mo ago", via: "Email",     note: "Requested new market update" },
  { role: "Past Client", name: "Mr Bertrand Osei",       company: "Private",              dealSlug: "canonbury",  when: "7mo ago", via: "Newsletter", note: "Opened last 4 newsletters" },
  { role: "Past Client", name: "Mrs Halina Zaborski",    company: "Private",              dealSlug: "ontario",    when: "9mo ago", via: "Referral",  note: "Referred her nephew" },
  { role: "Past Client", name: "Mr Ottilie Vandenberg",  company: "Private",              dealSlug: "battersea",  when: "10mo ago", via: "Phone",    note: "Second property enquiry" },
  { role: "Past Client", name: "Ms Constance Whitmore",  company: "Private",              dealSlug: "holland-park", when: "1y ago", via: "Instagram", note: "Testimonial published" },
  { role: "Past Client", name: "Mr Séamus Ó Braonáin",   company: "Private",              dealSlug: "highgate",   when: "1y ago",  via: "Email",     note: "Warranty period expired" },
  // ── Additional prospects (registered buyers & sellers) ────────
  { role: "Prospect", name: "Ms Delphine Aumerle",       company: "Registered buyer",     dealSlug: "chelsea",    when: "10h ago", via: "Property alert", note: "SW3 · £2m budget · cash" },
  { role: "Prospect", name: "Mr Otis Redfern",           company: "Registered buyer",     dealSlug: "canonbury",  when: "18h ago", via: "Phone",     note: "Viewing booked Thursday" },
  { role: "Prospect", name: "Dr Ines Fontaine",          company: "Registered seller",    dealSlug: "highgate",   when: "1d ago",  via: "Valuation", note: "Instructing sale next month" },
  { role: "Prospect", name: "Mr Ambrose Yardley",        company: "Registered buyer",     dealSlug: "battersea",  when: "2d ago",  via: "Portal",    note: "Requesting floor plans" },
  { role: "Prospect", name: "Ms Ottavia Bellucci",       company: "Registered buyer",     dealSlug: "limehouse",  when: "3d ago",  via: "Instagram", note: "Overseas buyer · relocating" },
  { role: "Prospect", name: "Mr & Mrs Torres",           company: "Registered seller",    dealSlug: "chingford",  when: "5d ago",  via: "Meeting",   note: "Downsize · looking Q4" },
  { role: "Prospect", name: "Dr Bastian Reuter",         company: "Registered buyer",     dealSlug: "ontario",    when: "1w ago",  via: "Email",     note: "Awaiting mortgage AIP" },
  { role: "Prospect", name: "Ms Genevieve Larkspur",     company: "Registered buyer",     dealSlug: "holland-park", when: "10d ago", via: "Newsletter", note: "Third viewing this month" },
  { role: "Prospect", name: "Mr Hamish Duncannon",       company: "Registered seller",    dealSlug: "chelsea",    when: "2w ago",  via: "Phone",     note: "Considering off-market listing" },
  { role: "Prospect", name: "Mrs Zara Sen-Gupta",        company: "Registered buyer",     dealSlug: "canonbury",  when: "4d ago",  via: "Email",     note: "Interested in Camden borders" },
  { role: "Prospect", name: "Mr Aleksy Chojnacki",       company: "Registered buyer",     dealSlug: "battersea",  when: "12d ago", via: "WhatsApp",  note: "Broker introduction · £1.4m" },
  { role: "Prospect", name: "Ms Rhiannon Trelawney",     company: "Registered seller",    dealSlug: "highgate",   when: "9d ago",  via: "Valuation", note: "Free market appraisal complete" },
  // ── Additional referrers & partners ───────────────────────────
  { role: "Referrer", name: "Mr Percival Ainsworth",     company: "Ainsworth Family Wealth", dealSlug: "chelsea",  when: "3d ago",  via: "Phone",     note: "Consistent quarterly referrer" },
  { role: "Referrer", name: "Ms Anthea Vogelstein",      company: "Vogelstein & Rees",     dealSlug: "highgate",   when: "5d ago",  via: "Email",     note: "Divorce coordination · repeat" },
  { role: "Referrer", name: "Mr Cyprian Odogwu",         company: "Odogwu Property Finance", dealSlug: "battersea", when: "6d ago", via: "Meeting",   note: "Buyer intro · £2.5m budget" },
  { role: "Referrer", name: "Mrs Persephone Halcrow",    company: "Halcrow Trust Advisors", dealSlug: "canonbury", when: "1w ago",  via: "Email",     note: "Family trust intro · repeat" },
  { role: "Referrer", name: "Ms Ionela Popescu",         company: "Popescu Immigration",   dealSlug: "limehouse",  when: "9d ago",  via: "WhatsApp",  note: "Investor visa buyer intro" },
  { role: "Referrer", name: "Mr Ruaraidh MacInnes",      company: "MacInnes Financial",    dealSlug: "ontario",    when: "2w ago",  via: "Phone",     note: "Bridging loan advisor" },
  { role: "Referrer", name: "Dr Zephaniah Blake",        company: "Blake Notaries",        dealSlug: "chingford",  when: "3w ago",  via: "Email",     note: "Notary · overseas signings" },
  { role: "Referrer", name: "Ms Marisol Delacroix",      company: "Delacroix Insurance",   dealSlug: "holland-park", when: "1mo ago", via: "Meeting", note: "Property insurance partner" },
  // ── Additional past clients ───────────────────────────────────
  { role: "Past Client", name: "Mr Rodrigo Fernández",   company: "Private",              dealSlug: "chelsea",    when: "3mo ago", via: "Referral",  note: "Referred two family members" },
  { role: "Past Client", name: "Mrs Aoife Nolan",        company: "Private",              dealSlug: "canonbury",  when: "4mo ago", via: "Instagram", note: "Featured in case study" },
  { role: "Past Client", name: "Dr Wilfred Amberley",    company: "Private",              dealSlug: "chingford",  when: "5mo ago", via: "Christmas card", note: "Sent card to whole team" },
  { role: "Past Client", name: "Ms Bronwen Cadwallader", company: "Private",              dealSlug: "ontario",    when: "8mo ago", via: "Phone",     note: "Property refurb complete" },
  { role: "Past Client", name: "Mr Théo Beauchamp",      company: "Private",              dealSlug: "battersea",  when: "2mo ago", via: "Email",     note: "Investment property enquiry" },
  { role: "Past Client", name: "Mrs Ottilia Rossetti",   company: "Private",              dealSlug: "highgate",   when: "6mo ago", via: "Newsletter", note: "Considering buy-to-let addition" },
  { role: "Past Client", name: "Mr Nikodemus Halvorsen", company: "Private",              dealSlug: "limehouse",  when: "11mo ago", via: "Instagram", note: "Positive Google review" },
  { role: "Past Client", name: "Ms Beatrix Kavanagh",    company: "Private",              dealSlug: "holland-park", when: "7mo ago", via: "Referral", note: "Referred former colleague" },
  // ── Chain contacts (extended coverage) ────────────────────────
  { role: "Past Client", name: "Dr Anselm Rothschild",   company: "Upward chain · SW11",  dealSlug: "battersea",  when: "1w ago",  via: "Solicitor", note: "Their bank valuation returned" },
  { role: "Past Client", name: "Ms Fenella Ainsworth",   company: "Downward chain · N19", dealSlug: "highgate",   when: "1d ago",  via: "Phone",     note: "Coordinating exchange window" },
  { role: "Past Client", name: "Mr Ludovic Prévost",     company: "Upward chain · E14",   dealSlug: "limehouse",  when: "4d ago",  via: "Email",     note: "Their solicitor slow · chasing" },
  { role: "Past Client", name: "Mrs Ottalie Weatherby",  company: "Downward chain · SW3", dealSlug: "chelsea",    when: "8d ago",  via: "WhatsApp",  note: "First-time buyer · patient" },
  { role: "Past Client", name: "Mr Diarmuid Gallagher",  company: "Upward chain · N6",   dealSlug: "highgate",   when: "11d ago", via: "Meeting",   note: "Buyer of vendor's next property" },
  { role: "Past Client", name: "Ms Yseult Ferrars",      company: "Downward chain · E4",  dealSlug: "chingford",  when: "6d ago",  via: "Phone",     note: "First-time buyer below · slow" },
  // ── Investor / landlord clients ───────────────────────────────
  { role: "Past Client", name: "Mr Sebastian Ashkenazi",  company: "Private landlord",     dealSlug: "canonbury",  when: "2mo ago", via: "Portfolio review", note: "Owns 6 flats · rotates every 3y" },
  { role: "Past Client", name: "Ms Aurelia Fitzhenry",    company: "Fitzhenry Holdings",   dealSlug: "battersea",  when: "5mo ago", via: "Meeting",   note: "Family office · four purchases with us" },
  { role: "Prospect",    name: "Dr Isadora Blackthorn",   company: "Registered buyer",     dealSlug: "highgate",   when: "1d ago",  via: "Portal",    note: "Cash buyer · under offer elsewhere" },
  { role: "Prospect",    name: "Mr Kenji Nakamura",       company: "Registered buyer",     dealSlug: "limehouse",  when: "2d ago",  via: "Email",     note: "Overseas · needs remote signing" },
  { role: "Prospect",    name: "Ms Élodie Beauchamps",    company: "Registered seller",    dealSlug: "chelsea",    when: "6d ago",  via: "Valuation", note: "Deceased estate · probate sale" },
  { role: "Referrer",    name: "Ms Cordelia Whitcombe",   company: "Whitcombe Private Bank", dealSlug: "chelsea",  when: "4d ago",  via: "Meeting",   note: "Private bank client intros · £3m+" },
  { role: "Referrer",    name: "Mr Anselm Tregarthen",    company: "Tregarthen Family Office", dealSlug: "holland-park", when: "1w ago", via: "Email", note: "Trust structure specialist" },
  { role: "Past Client", name: "Mr Emeric Sokolov",       company: "Private",              dealSlug: "ontario",    when: "1mo ago", via: "Phone",     note: "Refinance advisor recommendation" },
];


// iter-alignment · P44 — Operational-priority stage ordering.
// Higher-priority stages should surface first on the People page
// so operators triage matters closest to exchange fastest.  Uses
// the SAME `deal.stage` values the rail resolves — no separate
// stage table.
const _MATTER_PRIORITY = {
  exchange_ready: 0, exchanged: 0,           // exchange in flight / done
  enquiries: 1,
  bsa_pending: 2, mgmt_pack_pending: 2,
  solicitors: 3, contract: 3, searches: 3, draft_contract: 3,
  survey: 3,
  mortgage_app: 4, mortgage_offer: 4,
  aml: 5, memo: 5, offer: 5,
  completed: 99,                             // completed slides to bottom of "active" pool
};
const _matterPriority = (stageKey) => (
  Object.prototype.hasOwnProperty.call(_MATTER_PRIORITY, stageKey)
    ? _MATTER_PRIORITY[stageKey]
    : 6
);
const _TERMINAL_STAGES = new Set(["exchanged", "completed"]);

// iter-alignment · P44 — Derive a concise operator-facing
// responsibility line from the SAME SSOT the rail uses (deal.stage
// + deal.blockerState).  No new endpoints, no hardcoded per-deal
// copy — just the shared blocker/stage signals ChainFlow already
// carries.
const _matterResponsibility = (deal) => {
  const blocker = (deal.blockerState || "").trim();
  if (blocker) return blocker;             // pilot blocker copy already reads as an operator line
  const stage = deal.stage;
  if (stage === "exchange_ready") return "Ready to exchange";
  if (stage === "exchanged")      return "Awaiting completion";
  if (stage === "completed")      return "Completed";
  if (stage === "enquiries")      return "Enquiries in flight";
  if (stage === "bsa_pending")    return "BSA review pending";
  if (stage === "mgmt_pack_pending") return "Waiting for management pack";
  if (stage === "solicitors" || stage === "contract" || stage === "draft_contract") return "Contract stage in progress";
  if (stage === "searches")       return "Awaiting searches";
  if (stage === "survey")         return "Survey in progress";
  if (stage === "mortgage_app")   return "Mortgage application in flight";
  if (stage === "mortgage_offer") return "Mortgage offer received";
  return "In progress";
};

const _aggregatePeople = () => {
  const byKey = new Map();
  for (const deal of PILOT_DEALS) {
    const stagePill = _DEAL_STAGE_LABEL[deal.stage] || "Under Offer";
    const isExchangeReady = ["mortgage_offer", "exchange_ready", "exchanged", "completed"].includes(deal.stage);
    const isAtRisk = deal.status === "At Risk";
    const isTerminal = _TERMINAL_STAGES.has(deal.stage);
    const blockerNote = deal.blockerState && isAtRisk ? `${deal.blockerState} · ${deal.name}` : "";
    for (const p of (deal.participants || [])) {
      const company = p.organisation || (p.role === "Buyer" || p.role === "Seller" ? "Private" : "");
      const key = `${p.role}::${p.name}`;
      const existing = byKey.get(key) || {
        id: `p-${_personSlug(p.name)}`,
        name: p.name,
        role: _ROLE_TO_GROUP[p.role] || "buyer",
        roleLabel: p.role,
        company,
        // iter-366B — phone / email carried forward so the CRM
        // action icons resolve to real values across every row.
        phone: p.phone || null,
        email: p.email || null,
        active: true,
        deals: [],
        exchangeReadyCount: 0,
        blockerNotes: [],
        atRiskCount: 0,
      };
      // iter-alignment · P44 — carry raw `stage` + derived
      // responsibility + terminal flag so the People UI can sort
      // by operational priority and hide completed matters
      // without losing them (used as a fallback when a person has
      // zero live matters).
      existing.deals.push({
        dealId: deal.id,
        prop: deal.name,
        stage: stagePill,
        stageKey: deal.stage,
        responsibility: _matterResponsibility(deal),
        terminal: isTerminal,
      });
      if (isExchangeReady) existing.exchangeReadyCount += 1;
      if (blockerNote) existing.blockerNotes.push(blockerNote);
      if (isAtRisk) existing.atRiskCount += 1;
      byKey.set(key, existing);
    }
  }
  /* iter-366B — realistic per-person "last contact" line.
   *
   * The Deal-Room participant carries its own `updated_at` /
   * blocker cadence — surface a short phrase per person derived
   * from their role and their deals' status so the CRM feels
   * lived-in.
   *
   * Deterministic (hash-driven) so screenshots + tests stay
   * stable. */
  const _CHANNELS = ["Phone", "Email", "WhatsApp", "SMS"];
  const _lastContactFor = (row) => {
    if (row.deals.length === 0) return { channel: "Email", label: "Last active 3w ago" };
    const seed = _hashCode(row.name + row.role);
    const channel = _CHANNELS[seed % _CHANNELS.length];
    // Weight recency by activity: at-risk / exchange-ready contacts
    // tend to have more recent touches.
    let bucket = seed % 100;
    if (row.blockerNotes.length > 0)      bucket = bucket % 40;   // 0-39 hrs (fresh chases)
    else if (row.exchangeReadyCount > 0)  bucket = bucket % 48;   // 0-47 hrs
    else                                   bucket = 12 + (bucket % 240); // 12-252 hrs
    let when;
    if (bucket < 2)      when = "just now";
    else if (bucket < 24) when = `${bucket}h ago`;
    else if (bucket < 48) when = "yesterday";
    else if (bucket < 168) when = `${Math.floor(bucket / 24)}d ago`;
    else                  when = `${Math.floor(bucket / 168)}w ago`;
    return { channel, label: `${channel} · ${when}` };
  };

  // Shape into PeoplePage row contract.
  const rows = [];
  for (const r of byKey.values()) {
    // iter-alignment · P44 — Sort matters by operational priority
    // (Exchange → Enquiries → Contract → Mgmt Pack/BSA → Mortgage
    // → Offer).  Terminal (Completed) matters slide to the tail
    // so live ones surface first.
    const sortedDeals = [...r.deals].sort(
      (a, b) => _matterPriority(a.stageKey) - _matterPriority(b.stageKey),
    );
    const liveDeals = sortedDeals.filter((d) => !d.terminal);
    const completedDeals = sortedDeals.filter((d) => d.terminal);
    // iter-alignment · P44 — "No active matters" ONLY when zero
    // live; otherwise fall back to the most recent completed
    // matter so the person is never blank in the CRM.
    const visibleSource = liveDeals.length > 0 ? liveDeals : completedDeals.slice(0, 1);
    const visibleDeals = visibleSource.slice(0, 3);
    const moreDeals = Math.max(0, liveDeals.length - visibleDeals.length);
    const activeCount = liveDeals.length;
    const totalDeals = r.deals.length;
    const exchSuffix = r.exchangeReadyCount > 0
      ? ` · ${r.exchangeReadyCount} exchange ready`
      : "";
    const ctxTitle = totalDeals === 0
      ? "No active matters"
      : activeCount === 0
        ? "Most recent completed matter"
        : `${activeCount} active matter${activeCount === 1 ? "" : "s"}${exchSuffix}`;
    const ctxNote = r.blockerNotes[0] || "";
    const ctxTone = r.blockerNotes.length > 0 ? "amber" : (r.exchangeReadyCount > 0 ? "green" : "neutral");
    const badge = r.exchangeReadyCount >= 2 ? "Exchange expert"
                : r.atRiskCount >= 1        ? "Watch"
                : totalDeals >= 3           ? "Highly responsive"
                : totalDeals >= 2           ? "Fast responder"
                : totalDeals === 1          ? "Engaged"
                :                             "Standard";
    const contact = _lastContactFor(r);
    rows.push({
      id: r.id,
      name: r.name,
      role: r.role,
      roleLabel: r.roleLabel,
      company: r.company,
      phone: r.phone,
      email: r.email,
      badge,
      active: activeCount > 0,
      activeCount,
      deals: visibleDeals,
      moreDeals,
      ctxTitle,
      ctxNote,
      ctxLast: contact.label,
      ctxChannel: contact.channel,
      ctxTone,
    });
  }

  /* iter-366B — Fold in the extended CRM contacts (past clients,
   * prospects, referrers, chain contacts). Each row links to a
   * real pilot deal so the CRM stays consistent with the rest of
   * the pilot data. */
  const _dealByPilotSlug = Object.fromEntries(PILOT_DEALS.map((d) => [d.slug, d]));
  for (const c of _EXTENDED_CONTACTS) {
    const anchor = _dealByPilotSlug[c.dealSlug] || PILOT_DEALS[0];
    const isPastClient = c.role === "Past Client";
    const isReferrer   = c.role === "Referrer";
    const isProspect   = c.role === "Prospect";
    const anchorStage  = isPastClient ? "Exchanged"
                       : isReferrer   ? "Referring"
                       : isProspect   ? "Registered"
                       : "Prospect";
    const anchorId = `p-${_personSlug(c.name)}`;
    const anchorPhone = _phoneFor(anchorId);
    const anchorEmail = _emailFor(c.name, c.company, c.role);
    rows.push({
      id: anchorId,
      name: c.name,
      role: _ROLE_TO_GROUP[c.role] || "past_client",
      roleLabel: c.role,
      company: c.company || "",
      phone: anchorPhone,
      email: anchorEmail,
      badge: isPastClient ? "Past client"
           : isReferrer   ? "Referrer"
           : isProspect   ? "Prospect"
           :                "Standard",
      active: isProspect || isReferrer,
      activeCount: isProspect || isReferrer ? 1 : 0,
      deals: [{ dealId: anchor.id, prop: anchor.name, stage: anchorStage, stageKey: anchor.stage, responsibility: _matterResponsibility(anchor), terminal: _TERMINAL_STAGES.has(anchor.stage) }],
      moreDeals: 0,
      ctxTitle: isPastClient ? "Completed transaction"
              : isReferrer   ? "Introductions channel"
              : isProspect   ? "Awaiting the right match"
              :                "CRM contact",
      ctxNote: c.note || "",
      ctxLast: `${c.via || "Email"} · ${c.when || "recent"}`,
      ctxChannel: c.via || "Email",
      ctxTone: isReferrer ? "green" : isPastClient ? "neutral" : "neutral",
    });
  }

  // Sort: solicitors → buyers → sellers → brokers → surveyors →
  // managing_agent → agents → referrers → past_client → prospects
  const ORDER = {
    solicitor: 0, buyer: 1, seller: 2, broker: 3,
    surveyor: 4, managing_agent: 5, agent: 6,
    referrer: 7, past_client: 8, prospect: 9,
  };
  rows.sort((a, b) => {
    const oa = ORDER[a.role] ?? 9;
    const ob = ORDER[b.role] ?? 9;
    if (oa !== ob) return oa - ob;
    return b.deals.length - a.deals.length;
  });
  return rows;
};

export const PILOT_PEOPLE = Object.freeze(
  _aggregatePeople().map((r) => Object.freeze(r))
);

/* Pinned shortlist — top 3 most-active operational contacts. */
export const PILOT_PEOPLE_PINNED = Object.freeze(
  PILOT_PEOPLE
    .filter((p) => p.role === "solicitor" || p.role === "managing_agent")
    .slice(0, 3)
    .map((p) => Object.freeze({ id: p.id, name: p.name, company: p.company }))
);

/* Recently contacted — calibrated from highest-volume contacts. */
export const PILOT_PEOPLE_RECENT = Object.freeze(
  PILOT_PEOPLE.slice(0, 3).map((p, i) =>
    Object.freeze({
      id: p.id,
      name: p.name,
      when: i === 0 ? "1h ago" : i === 1 ? "3h ago" : "1d ago",
    })
  )
);

/* Quick insights — derived counts. */
const _delayingSolicitorCount = PILOT_PEOPLE.filter(
  (p) => p.role === "solicitor" && p.ctxTone === "amber"
).length;
const _highVolumeBrokerCount = PILOT_PEOPLE.filter(
  (p) => p.role === "broker" && p.deals.length >= 2
).length;
const _inactiveCount = PILOT_PEOPLE.filter((p) => p.deals.length === 0).length;

export const PILOT_PEOPLE_INSIGHTS = Object.freeze([
  Object.freeze({
    tone: "violet",
    icon: "Users",
    title: `${_delayingSolicitorCount || 0} solicitor${_delayingSolicitorCount === 1 ? "" : "s"} delaying exchanges`,
    cta: "View blockers",
  }),
  Object.freeze({
    tone: "amber",
    icon: "Activity",
    title: `${_highVolumeBrokerCount || 0} broker${_highVolumeBrokerCount === 1 ? "" : "s"} have high deal volume`,
    cta: "Review capacity",
  }),
  Object.freeze({
    tone: "rose",
    icon: "Users",
    title: `${_inactiveCount} person inactive · refresh outreach`,
    cta: "Send a follow up",
  }),
]);

/* Network overview counts. */
export const PILOT_PEOPLE_COUNTS = Object.freeze({
  total: PILOT_PEOPLE.length,
  activeThisWeek: PILOT_PEOPLE.filter((p) => p.active).length,
  exchangeReady: PILOT_PEOPLE.filter((p) =>
    p.deals.some((d) => d.stage === "Pre-Exchange" || d.stage === "Exchanged")
  ).length,
  delaying: PILOT_PEOPLE.filter((p) => p.ctxTone === "amber").length,
});

/* ─── iter-270I · iter-284 · Per-deal document request seed ─────
 *
 * Seeds per-deal document requests so the Documents Hub
 * "Pending Requests" surface, the Deal Room blocker rail, the
 * iter-281 health scorer (`outstandingDocs` signal), the
 * iter-282 timeline automation, the iter-283 Forecasting Centre
 * pressure rail, and the iter-284 chasing engine all have
 * realistic, deterministic operational content for pilot.
 *
 * Iter-284 expands the seed across health bands:
 *
 *   • Watch deals (silent ≥ T+3, chase #1 due) — searches
 *     outstanding, survey follow-up, mortgage offer pending.
 *   • At-Risk deals (silent ≥ T+5 or T+7, final escalation) —
 *     Management Pack outstanding (leasehold), enquiries silent,
 *     searches delayed by council.
 *   • Healthy deals carry NO seed requests so the canonical
 *     "pending" pile only fires on deals that genuinely need
 *     attention.
 *
 * `daysAgo` controls the request's age. Combined with the
 * iter-284 chasing engine, this produces a deterministic mix of
 * T+3 / T+5 / T+7 chases on initial pilot load.
 *
 * Calling code reads `PILOT_DEAL_DOC_REQUESTS[dealId]` for the
 * live list. */
const _DOC_REQUEST_SEEDS = {
  // canary wharf — LEASEHOLD · At-Risk · Management Pack outstanding T+11
  "c9a0000f-c9a0-4c9a-8c9a-c9a00000000f": [
    { template_id: "management_pack", title: "Management Pack requested",
      doc_label: "Management Pack", requested_from_role: "Seller Solicitor",
      requested_from_name: "Sarah Patel · Patel & Co",
      note: "EWS1 + service charge accounts — 11d overdue", daysAgo: 11 },
  ],
  // limehouse — LEASEHOLD · At-Risk · survey queries unresolved T+14
  "c9a00011-c9a0-4c9a-8c9a-c9a000000011": [
    { template_id: "survey_report", title: "Survey Report follow-up requested",
      doc_label: "Survey Report", requested_from_role: "Seller Solicitor",
      requested_from_name: "Mark Foley · Foley & Reeves",
      note: "Damp survey queries unresolved 14d", daysAgo: 14 },
    { template_id: "enquiries_responses", title: "Leasehold enquiries response",
      doc_label: "Leasehold Enquiries Response", requested_from_role: "Seller Solicitor",
      requested_from_name: "Mark Foley · Foley & Reeves",
      note: "Standard pre-contract enquiries · 8d silent", daysAgo: 8 },
  ],
  // blackheath — LEASEHOLD · At-Risk · searches stuck at council T+21
  "c9a00012-c9a0-4c9a-8c9a-c9a000000012": [
    { template_id: "searches", title: "Local authority searches requested",
      doc_label: "Local Authority Searches", requested_from_role: "Buyer Solicitor",
      requested_from_name: "Priya Aziz · Aziz Legal",
      note: "Council unresponsive · chase scheduled", daysAgo: 21 },
  ],
  // greenwich — FREEHOLD · Watch · searches T+4 (first chase due)
  "c9a00001-c9a0-4c9a-8c9a-c9a000000001": [
    { template_id: "searches", title: "Local authority searches requested",
      doc_label: "Local Authority Searches", requested_from_role: "Buyer Solicitor",
      requested_from_name: "Priya Aziz · Aziz Legal",
      note: "Standard 10-day turnaround · day 4", daysAgo: 4 },
  ],
  // earls court — LEASEHOLD · Watch · Management Pack T+5 (chase #2 due)
  "c9a00004-c9a0-4c9a-8c9a-c9a000000004": [
    { template_id: "management_pack", title: "Management Pack requested",
      doc_label: "Management Pack", requested_from_role: "Seller Solicitor",
      requested_from_name: "Sarah Patel · Patel & Co",
      note: "Awaiting service charge schedule", daysAgo: 5 },
  ],
  // putney — FREEHOLD · Watch · mortgage offer follow-up T+6
  "c9a00003-c9a0-4c9a-8c9a-c9a000000003": [
    { template_id: "mortgage_offer", title: "Mortgage offer copy requested",
      doc_label: "Mortgage Offer", requested_from_role: "Mortgage Broker",
      requested_from_name: "Tom Whittle · Whittle Lending",
      note: "Lender extension chased", daysAgo: 6 },
  ],
  // westferry — LEASEHOLD · Watch · enquiries silent T+3 (chase #1 due)
  "c9a00005-c9a0-4c9a-8c9a-c9a000000005": [
    { template_id: "enquiries_responses", title: "Pre-contract enquiries response",
      doc_label: "Pre-Contract Enquiries", requested_from_role: "Seller Solicitor",
      requested_from_name: "Mark Foley · Foley & Reeves",
      note: "Standard CO1/CO2 enquiries", daysAgo: 3 },
  ],
};

export const PILOT_DEAL_DOC_REQUESTS = Object.freeze(
  Object.fromEntries(
    Object.entries(_DOC_REQUEST_SEEDS).map(([dealId, list]) => [
      dealId,
      Object.freeze(list.map((r, i) => Object.freeze({
        id: `${dealId}-docreq-seed-${i}`,
        deal_id: dealId,
        template_id: r.template_id,
        title: r.title,
        doc_label: r.doc_label,
        requested_from_role: r.requested_from_role,
        requested_from_name: r.requested_from_name || null,
        note: r.note,
        status: "pending",
        created_at: _isoDaysAgo(r.daysAgo),
        fulfilled_at: null,
        fulfilled_doc_id: null,
        // iter-284 — chase_history is mutable on the live (user)
        // record; the seed always ships empty so the chasing
        // engine deterministically materialises pending chases on
        // first render.
        chase_history: [],
        _seed: true,
      }))),
    ])
  )
);
